import {
  DEFAULT_THEME,
  AVMED_THEME as avmedTheme,
  DefaultMetadataAvmed,
  DefaultMetadataSentara,
} from "@sentara-health/optimahealth_web";

import {
  FooterNavigation,
  ProfileMenuLinks,
  TopNavLinks,
  avMedBaseUrl,
  memberBaseUrl,
  memberBrokerContactUs,
} from "../constants/Navigation";
import {
  BrandLogoImages,
  BrandBannerImages,
  BrandBannerTitle,
  BrandBannerDesc,
  BrandBannerButtonText,
  LINK,
} from "../../utils/constants/Constants";
import { BrandsType } from "../../utils/models/CommonTypes";

type OidcConfig = {
  client_id: string | undefined;
  client_secret?: string;
};

const createOidcConfig = (
  clientId: string | undefined,
  clientSecret?: string
): OidcConfig => {
  const config: OidcConfig = { client_id: clientId };
  if (clientSecret?.length) config.client_secret = clientSecret;
  return config;
};

const avmedOidcConfig = createOidcConfig(
  process.env.REACT_APP_AVMED_EMPLOYER_OIDC_CLIENT_ID,
  process.env.REACT_APP_AVMED_EMPLOYER_OIDC_CLIENT_SECRET
);

const sentaraOidcConfig = createOidcConfig(
  process.env.REACT_APP_SENTARA_EMPLOYER_OIDC_CLIENT_ID,
  process.env.REACT_APP_SENTARA_EMPLOYER_OIDC_CLIENT_SECRET
);

export const BRAND_CONFIG: BrandsType = {
  AVMED: {
    brandName: "AVMED",
    hostNames: [
      "dev.employer.avmed.com",
      "qa.employer.avmed.com",
      "reg.employer.avmed.com",
      "uat.employer.avmed.com",
      "employer.avmed.com",
    ],
    footerNavigation: {
      ...{
        footerTermsOfUse: {
          ...FooterNavigation.footerTermsOfUse,
          url: `https://www.avmed.org/terms-of-use/`,
        },
        footerPrivacy: {
          dataTestId: "footerPrivacyPolicy",
          label: "footer.label.privacyPolicy",
          url: `https://www.avmed.org/your-privacy`,
          target: "_blank",
        },
        footerLangAssist: {
          ...FooterNavigation.footerLangAssist,
          url: `https://www.avmed.org/accessibility-statement`,
        },
        footerNoticeOfNonDiscrimination: {
          ...FooterNavigation.footerNoticeOfNonDiscrimination,
          url: `https://www.avmed.org/notice-of-nondiscrimination`,
        },
        footerContactUs: {
          dataTestId: "footerContactUs",
          label: "footer.label.contactUs",
          url: `${avMedBaseUrl}/contact-us`,
          target: "_blank",
        },
      },
    },
    profileMenuLinks: {
      ...ProfileMenuLinks,
      ...{
        navLinkProfile: {
          ...ProfileMenuLinks.navLinkProfile,
          to: `${avMedBaseUrl}/web/business/employers/`,
        },
      },
    },
    topNavLinks: {
      ...TopNavLinks,
      navTopEmployerHome: {
        ...TopNavLinks.navTopEmployerHome,
        to: `${avMedBaseUrl}/web/business/employers/`,
      },
    },
    memberBrokerContactUs: `${avMedBaseUrl}/contact-us `,
    homeLink: `${avMedBaseUrl}/web/business/employers/`,
    link: {
      privacyPolicy: LINK.PRIVACY_POLICY_AVMED,
      homePageLink: LINK.AGENTHOME_LINK,
      sorc: "mailto:AvMedIFPMAgentSupport@avmed.org",
      sorc1: "mailto:smallgroupsupport@avmed.org",
      dashboardLearnMoreLink: LINK.LEARN_MORE_LINK_DASHBOARD_AVMED, // NB: Need a link for learn more Avmed
      supportTileLink: LINK.CONTACT_US_DASHBOARD_BRAND,
    },
    oidcConfig: avmedOidcConfig,
    image: {},
    logoImage: BrandLogoImages.avmed_v2,
    bannerImage: BrandBannerImages.avmed,
    bannerMobileImage: BrandBannerImages.avmed_mobile,
    bannerBackgroundColor: BrandBannerImages.avmedBackgroundColor,
    bannerBackgroundSize: BrandBannerImages.avmedBackgroundSize,
    bannerTitle: BrandBannerTitle.avmed,
    bannerDesc: BrandBannerDesc.avmed,
    bannerButtonText: BrandBannerButtonText.avmed,
  },
  SENTARAHEALTHPLANS: {
    brandName: "SENTARAHEALTHPLANS",
    adfsName: "DEFAULT",
    footerNavigation: FooterNavigation,
    topNavLinks: TopNavLinks,
    profileMenuLinks: ProfileMenuLinks,
    memberBrokerContactUs: memberBrokerContactUs,
    homeLink: `${memberBaseUrl}/employers/`,
    link: {
      privacyPolicy: LINK.PRIVACY_POLICY,
      homePageLink: LINK.EMPLOYERHOME_LINK,
      sorc: "mailto:webmaster@sentara.com",
      dashboardLearnMoreLink: LINK.LEARN_MORE_LINK_DASHBOARD,
      supportTileLink: LINK.CONTACT_US_DASHBOARD,
    },
    oidcConfig: sentaraOidcConfig,
    image: {},
    logoImage: BrandLogoImages.shp,
    bannerImage: BrandBannerImages.shp,
    bannerMobileImage: BrandBannerImages.shp_mobile,
    bannerBackgroundColor: BrandBannerImages.shpBackgroundColor,
    bannerBackgroundSize: BrandBannerImages.shpBackgroundSize,
    bannerTitle: BrandBannerTitle.shp,
    bannerDesc: BrandBannerDesc.shp,
    bannerButtonText: BrandBannerButtonText.shp,
  },
};

export type Theme = typeof DEFAULT_THEME;

export const SHP_THEME: Theme = {
  ...DEFAULT_THEME,
  metadata: {
    ...DefaultMetadataSentara,
    title: "Employers Link | Sentara Health Plans",
    description: "Employers links for the Sentara Health Plans website.",
    keywords: [
      "virginia",
      "health insurance",
      "individual",
      "group",
      "medicare",
      "optima health",
      "Sentara Health Plans",
    ],
  },
  // footerLinks: {
  //   background: "#f5f0ee",
  //   fontFamily: `600 1rem "RedHatDisplay_semibold", Helvetica, Arial, sans-serif`,
  //   text: "#4B4849",
  // },
  topNavigation: {
    ...DEFAULT_THEME.topNavigation,
    active: {
      borderBottom: "0.125rem solid #171717",
      text: "#171717",
    },
    hovered: {
      ...DEFAULT_THEME.topNavigation.hovered,
      borderBottom: "0.125rem solid #171717",
      text: "#171717",
    },
    font: `400 1rem "RedHatDisplay_regular", Helvetica, Arial, sans-serif`,
    text: "#8d8585",
  },
  dropdownButton: {
    ...DEFAULT_THEME.dropdownButton,
    text: "#227066",
  },
  buttonDropdown: {
    ...DEFAULT_THEME.buttonDropdown,
    text: "#227066",
  },
  commonText: {
    ...DEFAULT_THEME.commonText,
    headings: {
      ...DEFAULT_THEME.commonText.headings,
      h1: "#000000",
      h1FontFamilies: {
        ...DEFAULT_THEME.commonText.headings.h1FontFamilies,
        medium: `600 40px "RedHatDisplay_semibold", Georgia, Times, serif`,
      },
      h2: "#000000",
      h3: "#000000",
      h4: "#000000",
      h5: "#000000",
      h6: "#000000",
    },
    paragraphs: {
      ...DEFAULT_THEME.commonText.paragraphs,
      xSmall: "#000000",
      small: "#000000",
      medium: "#000000",
      large: "#000000",
      xLarge: "#000000",
      // mediumFont: {
      //   ...DEFAULT_THEME.commonText.paragraphs.mediumFont,
      //   body: `400 16px "RedHatDisplay_body", Helvetica, Arial, sans-serif`,
      // },
    },
    links: {
      ...DEFAULT_THEME.commonText.links,
      navLeft: {
        ...DEFAULT_THEME.commonText.links.navLeft,
        color: "#171717",
        // font: `400 14px "RedHatDisplay_body", Helvetica, Arial, sans-serif`,
        active: {
          ...DEFAULT_THEME.commonText.links.navLeft.active,
          color: "#171717",
        },
        hovered: {
          ...DEFAULT_THEME.commonText.links.navLeft.hovered,
          color: "#171717",
        },
      },
    },
  },
  // globalWarning: {
  //   fontFamily: `400 16px "RedHatDisplay_body", Helvetica, Arial, sans-serif`,
  //   text: "#991b1b",
  // },
  accordion: {
    ...DEFAULT_THEME.accordion,
    text: "#171717",
  },
  commonTextColors: {
    primary: "#000000",
  },
  // table: {
  //   ...DEFAULT_THEME.table,
  //   font: `400 16px "RedHatDisplay_body", Helvetica, Arial, sans-serif`,
  //   headers: {
  //     ...DEFAULT_THEME.table.headers,
  //     font: `700 16px "RedHatDisplay_bold", Helvetica, Arial, sans-serif`,
  //     background: "#333131"
  //   },
  //   colHeaders: {
  //     ...DEFAULT_THEME.table.colHeaders,
  //     font: `700 16px "RedHatDisplay_bold", Helvetica, Arial, sans-serif`,
  //   },
  // },
};

export const AVMED_THEME: Theme = {
  ...avmedTheme,
  metadata: {
    ...DefaultMetadataAvmed,
    title: "AvMed Employer Portal",
    description: "Employers links for the AvMed website.",
    keywords: [],
  },
  commonText: {
    ...avmedTheme.commonText,
    headings: {
      ...avmedTheme.commonText.headings,
      h5: "#333131",
    },
    links:{
      ...avmedTheme.commonText.links,
      navLeft: {
        ...avmedTheme.commonText.links.navLeft,
        hovered: {
          ...avmedTheme.commonText.links.navLeft.hovered,
        }
      }
    }
  }
};

export const AVMED_THEME_ORIGINAL: Theme = {
  accordion: {
    background: "#f5f0ee",
    text: "#171717",
  },
  accordionHeader: {
    background: "#333131",
    text: "#fff",
    fontSize: "1.25rem",
    fontWeight: "700",
    fontFamily: '"RedHatDisplay_bold", Helvetica, Arial, sans-serif',
  },
  accordionFAQ: {
    backgroundColor: "#fff",
    iconStroke: "#03646f",
    text: "#03646f",
  },
  alert: {
    danger: {
      background: "rgb(237, 7, 7)",
      border: "rgb(241, 174, 181)",
      icons: "#fff",
      links: "#fff",
      text: "#fff",
      title: "#fff",
    },
    fontFamily: '"RedHatDisplay_body", Helvetica, Arial, sans-serif',
    info: {
      background: "rgb(207, 244, 252)",
      border: "rgb(158, 234, 249)",
      icons: "#002F43",
      links: "#002F43",
      text: "rgb(5, 81, 96)",
      title: "#002F43",
    },
    success: {
      background: "rgb(209, 231, 221)",
      border: "rgb(163, 207, 187)",
      icons: "#166534",
      links: "#166534",
      text: "rgb(10, 54, 34)",
      title: "#166534",
    },
    titleFontFamily: '"RedHatDisplay_medium", Helvetica, Arial, sans-serif',
    warning: {
      background: "rgb(255, 243, 205)",
      border: "rgb(255, 230, 156)",
      icons: "#92400E",
      links: "#92400E",
      text: "rgb(102, 77, 3)",
      title: "#92400E",
    },
  },

  button: {},
  buttonDropdown: {
    active: {
      background: "#fcfafa",
      border: "#eee7e4",
      text: "#171717",
    },
    background: "#0000",
    border: "#eee7e4",
    disabled: {
      background: "#a7c6c2",
      border: "#a7c6c2",
      text: "#fff",
    },
    focused: {
      background: "#fcfafa",
      border: "#eee7e4",
      text: "#171717",
    },
    fontFamily: '"RedHatDisplay_bold", Helvetica, Arial, sans-serif',
    hovered: {
      background: "#fcfafa",
      border: "#eee7e4",
      text: "#171717",
    },
    text: "#212529",
  },
  buttonLink: {
    background: "#fff",
    fontFamily: '"RedHatDisplay_bold", Helvetica, Arial, sans-serif',
    header: {
      background: "#fff",
      text: "#262626",
      icon: "#262626",
    },
    icon: "#03646f",
    text: "#03646f",
    underline: "#03646f",
  },
  buttonPrimary: {
    active: {
      background: "#0a5057",
      border: "#03646f",
      text: "#a9cace",
    },
    background: "#03646f",
    border: "#03646f",
    disabled: {
      background: "#a9cace",
      border: "#a9cace",
      text: "#fff",
    },
    focused: {
      background: "#0a5057",
      border: "0a5057",
      text: "#fff",
    },
    fontFamily: '"RedHatDisplay_bold", Helvetica, Arial, sans-serif',
    hovered: {
      background: "#0a5057",
      border: "#0a5057",
      text: "#fff",
    },
    text: "#fff",
  },
  buttonSecondary: {
    active: {
      background: "#fff",
      border: "#50868c",
      // boxShadow: '#a7c6c2'
      text: "#50868c",
    },
    background: "#fff",
    border: "#03646f",
    disabled: {
      background: "#fff",
      border: "#a9cace",
      text: "#a9cace",
    },
    focused: {
      background: "#fff",
      border: "#0a5057",
      text: "#0a5057",
    },
    fontFamily: '"RedHatDisplay_bold", Helvetica, Arial, sans-serif',
    hovered: {
      background: "#fff",
      border: "#0a5057",
      text: "#0a5057",
    },
    text: "#03646f",
  },
  buttonTabs: {
    background: "#f5f0ee",
    border: "#f5f0ee",
    fontFamily: '"RedHatDisplay_bold", Helvetica, Arial, sans-serif',
    selected: {
      background: "#333131",
      border: "#333131",
      text: "#fff",
    },
    text: "#333131",
  },
  chatBubble: "#333131",
  checkbox: {
    background: "#fff",
    border: "#8d8585",
    fontFamily: '"RedHatDisplay_medium", Helvetica, Arial, sans-serif',
    selected: {
      checkbox: "#03646f",
    },
    text: "#171717",
  },
  commonText: {
    disclaimers: {
      content: "#000",
      contentFontFamily: '"RedHatDisplay_body", Helvetica, Arial, sans-serif',
      title: "#000",
      titleFontFamily: '"RedHatDisplay_bold", Helvetica, Arial, sans-serif',
    },
    footerDisclaimers: "#333131",
    borderTopPrimary: "#a9edce",
    borderTopSecondary: "#b4c4fc",
    footerDisclaimersFontFamily:
      '"RedHatDisplay_semibold", Helvetica, Arial, sans-serif',
    headings: {
      h1: "#171717",
      h1FontFamilies: {
        black: '"RedHatDisplay_black", Helvetica, Arial, sans-serif',
        body: '"RedHatDisplay_body", Helvetica, Arial, sans-serif',
        bold: '"RedHatDisplay_bold", Helvetica, Arial, sans-serif',
        extraBold: '"RedHatDisplay_extraBold", Helvetica, Arial, sans-serif',
        light: '"RedHatDisplay_light", Helvetica, Arial, sans-serif',
        medium: '"Literata_medium", Georgia, Times, serif',
        regular: '"RedHatDisplay_regular", Helvetica, Arial, sans-serif',
        semiBold: '"RedHatDisplay_semibold", Helvetica, Arial, sans-serif',
      },
      h2: "#333131",
      h2FontFamilies: {
        black: '"RedHatDisplay_black", Helvetica, Arial, sans-serif',
        body: '"RedHatDisplay_body", Helvetica, Arial, sans-serif',
        bold: '"RedHatDisplay_bold", Helvetica, Arial, sans-serif',
        extraBold: '"RedHatDisplay_extraBold", Helvetica, Arial, sans-serif',
        light: '"RedHatDisplay_light", Helvetica, Arial, sans-serif',
        medium: '"RedHatDisplay_medium", Helvetica, Arial, sans-serif',
        regular: '"RedHatDisplay_regular", Helvetica, Arial, sans-serif',
        semiBold: '"RedHatDisplay_semibold", Helvetica, Arial, sans-serif',
      },
      h3: "#333131",
      h3FontFamilies: {
        black: '"RedHatDisplay_black", Helvetica, Arial, sans-serif',
        body: '"RedHatDisplay_body", Helvetica, Arial, sans-serif',
        bold: '"RedHatDisplay_bold", Helvetica, Arial, sans-serif',
        extraBold: '"RedHatDisplay_extraBold", Helvetica, Arial, sans-serif',
        light: '"RedHatDisplay_light", Helvetica, Arial, sans-serif',
        medium: '"RedHatDisplay_medium", Helvetica, Arial, sans-serif',
        regular: '"RedHatDisplay_regular", Helvetica, Arial, sans-serif',
        semiBold: '"RedHatDisplay_semibold", Helvetica, Arial, sans-serif',
      },
      h4: "#333131",
      h4FontFamilies: {
        black: '"RedHatDisplay_black", Helvetica, Arial, sans-serif',
        body: '"RedHatDisplay_body", Helvetica, Arial, sans-serif',
        bold: '"RedHatDisplay_bold", Helvetica, Arial, sans-serif',
        extraBold: '"RedHatDisplay_extraBold", Helvetica, Arial, sans-serif',
        light: '"RedHatDisplay_light", Helvetica, Arial, sans-serif',
        medium: '"RedHatDisplay_medium", Helvetica, Arial, sans-serif',
        regular: '"RedHatDisplay_regular", Helvetica, Arial, sans-serif',
        semiBold: '"RedHatDisplay_semibold", Helvetica, Arial, sans-serif',
      },
      h5: "#333131",
      h5FontFamilies: {
        black: '"RedHatDisplay_black", Helvetica, Arial, sans-serif',
        body: '"RedHatDisplay_body", Helvetica, Arial, sans-serif',
        bold: '"RedHatDisplay_bold", Helvetica, Arial, sans-serif',
        extraBold: '"RedHatDisplay_extraBold", Helvetica, Arial, sans-serif',
        light: '"RedHatDisplay_light", Helvetica, Arial, sans-serif',
        medium: '"RedHatDisplay_medium", Helvetica, Arial, sans-serif',
        regular: '"RedHatDisplay_regular", Helvetica, Arial, sans-serif',
        semiBold: '"RedHatDisplay_semibold", Helvetica, Arial, sans-serif',
      },
      h6: "#333131",
      h6FontFamilies: {
        black: '"RedHatDisplay_black", Helvetica, Arial, sans-serif',
        body: '"RedHatDisplay_body", Helvetica, Arial, sans-serif',
        bold: '"RedHatDisplay_bold", Helvetica, Arial, sans-serif',
        extraBold: '"RedHatDisplay_extraBold", Helvetica, Arial, sans-serif',
        light: '"RedHatDisplay_light", Helvetica, Arial, sans-serif',
        medium: '"RedHatDisplay_medium", Helvetica, Arial, sans-serif',
        regular: '"RedHatDisplay_regular", Helvetica, Arial, sans-serif',
        semiBold: '"RedHatDisplay_semibold", Helvetica, Arial, sans-serif',
      },
    },
    labels: "#333131",
    labelsFontFamily: '"RedHatDisplay_medium", Helvetica, Arial, sans-serif',
    links: "#03646f",
    linksFontFamily: '"RedHatDisplay_medium", Helvetica, Arial, sans-serif',
    paragraphs: "#333131",
    paragraphsFontFamily: '"RedHatDisplay_body", Helvetica, Arial, sans-serif',
    disabled: "#696363",
  },
  contactInfo: {
    text: "#03646f",
  },
  dropdownButton: {
    border: "#171717",
    background: "#fff",
    dropdownIcon: "#171717",
    icon: "#171717",
    text: "#171717",
  },
  footerLinks: {
    background: "#f5f0ee",
    fontFamily: '"RedHatDisplay_semibold", Helvetica, Arial, sans-serif',
    text: "#4b4849",
  },
  globalError: {
    backgroundColor: "#fef2f2",
    borderColor: "#fecaca",
    fontFamily: '"RedHatDisplay_body", Helvetica, Arial, sans-serif',
    text: "#991b1b",
  },
  globalSuccess: {
    backgroundColor: "#F0FDF4",
    borderColor: "#BBF7D0",
    fontFamily: '"RedHatDisplay_body", Helvetica, Arial, sans-serif',
    text: "#166534",
    successIcon: "#166534",
  },
  icons: {
    accordionDownArrow: "#03646f",
    agent: {
      backgroundCircle: "#CFD9E1",
      background: "#171717",
      foreground: "#fff",
    },
    apple: "#171717",
    billingStatement: "#171717",
    bookOfBusiness: "#171717",
    authorizations: "#171717",
    calendar: "#171717",
    chat: {
      background: "#171717",
      foreground: "#FAFAFA",
    },
    chatbot: {
      background: "#171717",
      foreground: "#fff",
    },
    chatbotClose: "#262626",
    chatBubble: "#262626",
    chatCircleDot: {
      background: "#171717",
      dot: "#22C55E",
      foreground: "#fff",
    },
    chatClose: "#262626",
    chatDot: {
      background: "#171717",
      dot: "#22C55E",
      foreground: "#fff",
    },
    chatInfo: "#262626",
    chatWeb: {
      background: "#E7ECF0",
      foreground: "#262626",
    },
    chatMinimize: "#171717",
    chevronDown: "#333131",
    chevronRight: "#333131",
    chevronUp: "#333131",
    checkBig: "#03646f",
    claims: "#171717",
    clock: {
      background: "#E7ECF0",
      foreground: "#262626",
    },
    close: "#171717",
    closeBlock: "#171717",
    connectingAgent: {
      selectedDot: "#171717",
      unselectedDot: "#A3A3A3",
    },
    customerSupport: "#171717",
    doctor: "#262626",
    document: "#171717",
    documentNote: "#171717",
    employerNew: "#171717",
    enrollmentHistory: "#171717",
    eyeClosed: "#171717",
    eyeOpen: "#171717",
    formsAndDocs: "#171717",
    groupInfo: "#171717",
    hamburger: "#171717",
    healthReimbursement: "#171717",
    healthSavings: "#171717",
    healthSavingsAccount: "#171717",
    help: "#171717",
    idCard: "#171717",
    individualNew: "#171717",
    info: "#333131",
    insurance: "#171717",
    loading: {
      selectedDot: "#171717",
      unselectedDot: "#A3A3A3",
    },
    mail: "#171717",
    medicareEnrollment: "#171717",
    money: "#171717",
    myPlan: "#171717",
    phone: "#212529",
    payMyPremium: "#171717",
    print: "#171717",
    profile: "#171717",
    quoteEnrollManage: "#171717",
    retry: "#171717",
    rightArrow: "#171717",
    search: "#171717",
    iconBlack: "#171717",
    settings: "#171717",
    snapshot: "#171717",
    stethoscope: "#171717",
    support: "#171717",
    taxForm: "#171717",
    umbrella: "#171717",
    urgentCareSearch: "#171717",
    userBlack: "#171717",
    userBlackFull: "#171717",
    userWhite: "#fff",
    virtualVisit: "#171717",
  },
  inlineError: {
    fontFamily: '"RedHatDisplay_body", Helvetica, Arial, sans-serif',
    text: "#dc2626",
  },
  input: {
    background: "#fff",
    border: "#696363",
    fontFamily: '"RedHatDisplay_body", Helvetica, Arial, sans-serif',
    icon: "rgb(51, 49, 49)",
    placeholder: "#696363",
    text: "#696363",
  },
  inputError: {
    border: "#ed0707",
  },
  inputFocused: {
    border: "#0467eb",
    boxShadow: "rgba(5, 93, 242, 0.3)",
    outline: "#0467eb",
  },
  link: "#03646f",
  loadingSpinner: "#03646f",
  modalOverlay: {
    closeButton: "#333131",
  },
  navigationMenu: {
    background: "transparent",
    border: "rgb(222, 226, 230)",
    dropdownIcon: "#171717",
    dropdownItems: {
      background: "#fff",
      hovered: {
        background: "rgb(248, 249, 250)",
        text: "rgb(33, 37, 41)",
      },
      text: "#171717",
    },
    text: "#171717",
  },
  planDropdown: {
    dropdownIcon: "#343132",
    text: "#171717",
  },
  profileCard: {
    headings: {
      color: "#333131",
      fontFamily: '"RedHatDisplay_bold", Helvetica, Arial, sans-serif',
    },
    syllablesCircle: {
      background: "#f5f0ee",
      fontFamily: '"RedHatDisplay_body", Helvetica, Arial, sans-serif',
      text: "#333131",
    },
    text: {
      color: "#333131",
      fontFamily: '"RedHatDisplay_body", Helvetica, Arial, sans-serif',
    },
  },
  circleProgressPrimary: {
    circle: {
      track: "#03646f",
      progress: "#a9edce",
    },
  },
  circleProgressSecondary: {
    circle: {
      track: "#b4c4fc",
      progress: "#3b3ba8",
    },
  },
  radio: {
    background: "#fff",
    border: "#8d8585",
    selected: {
      radio: "#03646f",
    },
  },
  select: {
    background: "#fff",
    border: "#696363",
    dropdown: "#0467eb",
    error: {
      border: "#ed0707",
    },
    focused: {
      border: "#0467eb",
      boxShadow: "rgba(5, 93, 242, 0.3)",
      outline: "#0467eb",
    },
    fontFamily: '"RedHatDisplay_body", Helvetica, Arial, sans-serif',
    placeholder: "#696363",
    text: "#696363",
  },
  stepper: {
    activeConnector: "#03646f",
    activeStep: {
      background: "#fff",
      border: "#03646f",
      boxShadow: "#d3e2e0",
      text: "#03646f",
      textFont: "#171717",
    },
    inactiveStep: {
      background: "#fff",
      border: "#d3e2e0",
      text: "#171717",
    },
    inactiveConnector: "#eee7e4",
    selectedStep: {
      background: "#03646f",
      border: "#eee7e4",
      boxShadow: "none",
      text: "#fff",
    },
  },
  submenu: {
    border: "#f5f0ee",
    links: {
      fontFamily: '"RedHatDisplay_body", Helvetica, Arial, sans-serif',
      text: "#171717",
    },
  },
  table: {
    headers: {
      background: "#333131",
      text: "#fff",
    },
    rows: {
      even: {
        background: "#fcfafa",
        text: "#000",
      },
      odd: {
        background: "transparent",
        text: "#000",
      },
    },
  },
  tabs: {
    background: "#fff",
    border: "#fff",
    focused: {
      border: "#e9ecef",
    },
    fontFamily: '"RedHatDisplay_bold", Helvetica, Arial, sans-serif',
    hovered: {
      border: "#f5f0ee",
    },
    selected: {
      background: "#fff",
      border: "#03646f",
      text: "#03646f",
    },
    text: "#171717",
  },
  textarea: {
    background: "#fff",
    border: "#696363",
    dropdown: "#0467eb",
    focused: {
      border: "#0467eb",
      boxShadow: "rgba(5, 93, 242, 0.3)",
      outline: "#0467eb",
    },
    fontFamily: '"RedHatDisplay_body", Helvetica, Arial, sans-serif',
    placeholder: "#696363",
    text: "#696363",
  },
  topNavigation: {
    background: "#fff",
    border: "#fff",
    fontFamily: '"RedHatDisplay_body", Helvetica, Arial, sans-serif',
    hovered: {
      background: "#f5f0ee",
      border: "#b3a9a3",
      text: "#171717",
    },
    text: "#8d8585",
  },
  collapsible: {
    text: "#fff",
    background: "#333131",
  },
};
